<template>
  <article
    data-testid="article-card"
    class="shadow-pm hover:shadow-pm-hover flex flex-col overflow-hidden rounded-xl bg-white transition-shadow md:flex-row"
  >
    <div
      data-testid="article-card-cover"
      class="h-24 w-full shrink-0 bg-cover bg-center md:h-full md:max-w-32"
      :style="{ backgroundImage: `url('${coverUrl}')` }"
    />
    <div
      class="relative flex flex-col justify-between gap-2 p-4 transition-all duration-300"
    >
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-x-3">
          <span
            v-if="withNewTag"
            class="bg-p-500 rounded-full px-4 py-2 text-sm font-semibold"
          >
            New
          </span>
          <p class="text-s-600 whitespace-nowrap text-xs font-semibold">
            <span data-testid="article-card-published-date">
              {{ formattedDate }} |
            </span>
            <span data-testid="article-card-service-name" class="capitalize">{{
              serviceName
            }}</span>
          </p>
        </div>

        <div>
          <NuxtLink :to="articleLink" no-prefetch>
            <h3
              data-testid="article-card-title"
              class="line-clamp-3 text-lg font-semibold md:line-clamp-2"
              :class="[!showDescription ? '' : 'lg:text-xl lg:font-bold']"
            >
              {{ title }}
            </h3>
          </NuxtLink>
        </div>
      </div>

      <div class="flex flex-col gap-5">
        <div
          v-if="showDescription"
          data-testid="article-card-description"
          class="line-clamp-4 text-sm md:text-base"
          v-html="excerpt"
        />

        <div
          class="flex flex-col items-start gap-2 lg:flex-row lg:items-center"
        >
          <div class="flex flex-wrap gap-x-2 gap-y-8 overflow-hidden">
            <CategoryPill
              v-for="category in articleCategories"
              :key="category.name"
              :category="category"
            />
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { RecordType } from '~/src/types/common'
import type { RouteLocationAsPath, LocationQueryRaw } from 'vue-router'
import { differenceInMonths, format, parseISO } from 'date-fns'
import CategoryPill from '~/components/price-assessments/CategoryPill.vue'

const props = withDefaults(
  defineProps<{
    title: string
    date: string
    excerpt?: string
    slug: string | number
    serviceBasePath?: string
    categories: { nodes: { name: string }[] }
    service: string
    queryString?: RecordType | string
    coverUrl?: string
    showDescription?: boolean
  }>(),
  {
    excerpt: '',
    serviceBasePath: '',
    queryString: '',
    coverUrl: '',
    showDescription: true,
  },
)

const formattedDate = computed(() =>
  format(new Date(props.date), 'dd MMM yyyy'),
)
const articleLink = computed(() => {
  const articleLink: RouteLocationAsPath = {
    path: `/${props.serviceBasePath || props.service}/${props.slug}`,
  }

  if (props.queryString !== '') {
    articleLink.query = props.queryString as LocationQueryRaw
  }

  return articleLink
})

const withNewTag = computed(() => {
  const now = Date.now()
  const date = parseISO(props.date)

  return differenceInMonths(date, now) === 0
})

const articleCategories = computed(() => {
  if (!props.categories?.nodes.length) {
    return []
  }
  return props.categories.nodes
})
const serviceName = computed(() => {
  return props.service.replace('-', ' ')
})
</script>
